import type { Constructor } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { firstValueFrom } from 'rxjs';
import { ProductEntryRequirement } from '../entities/ProductEntryRequirement.js';

export async function checkWeatherUserMeetsEntryRequirements(userID: string, productID: string) {
  const entryRequirementsData = await loadMongoData(ProductEntryRequirement, { productID });
  const entryRequirements = entryRequirementsData.map(e => {
    const node = baseqlMetaData.entities.find(node => (node.type() as typeof AdornisEntity)._class === e._class);
    const entity = node?.type() as Constructor<any>;
    return new entity(e);
  });

  for (const entryRequirement of entryRequirements) {
    const requirementIsMet = await entryRequirement.requirementIsMet(userID);
    if (!requirementIsMet) return { requirementsMet: false, reason: await entryRequirement.label() } as const;
  }

  return { requirementsMet: true } as const;
}

export const subscribeProductEntryRequirementsForProduct = registerSubscription({
  operationName: 'subscribeProductEntryRequirementsForProduct',
  resolve:
    (productID: string) =>
    <SelSet extends BaseQLSelectionSet<ProductEntryRequirement>>(gqlFields: SelSet) =>
      watchQuery<ProductEntryRequirement>(ProductEntryRequirement._collectionName, {
        productID,
      }),
  type: () => [ProductEntryRequirement],
  params: [{ name: 'productID', type: () => ID }],
});

export const checkWeatherUserMeetsEntryRequirementsQuery = registerQuery({
  operationName: 'checkWeatherUserMeetsEntryRequirements',
  resolve: (userID: string, productID: string) => async () => {
    const result = await checkWeatherUserMeetsEntryRequirements(userID, productID);
    if (!result.requirementsMet) {
      const errMesg = `Das Produkt kann nicht gebucht werden, da die Anforderung '${result.reason[0]}' nicht erfüllt wird.`;
      throw new Error(errMesg);
    }
    return true;
  },
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => String },
    { name: 'productID', type: () => String },
  ],
});

export const getProductEntryRequirementLabels = registerQuery({
  operationName: 'getProductEntryRequirementLabels',
  params: [{ name: 'productID', type: () => String }],
  type: () => [String],
  resolve: function (productID: string) {
    return async () => {
      if (!productID) return [];
      const requirements = await firstValueFrom(
        subscribeProductEntryRequirementsForProduct(productID)({
          ...ProductEntryRequirement.allFields,
        }),
      );
      const labels = Promise.all(requirements.map(r => r.label()));
      return labels;
    };
  },
});
